/*
 * Copyright (c) 2022 CalAmp Corp. All Rights Reserved
 */
import {useEffect, useState} from 'react';
import {translate} from '@ion/core/src/locale/locale';
import {timeFormatter} from '@ion/core/src/utils/DateUtil';
import useEntityLabel from '../hooks/useEntityLabel';

export interface UseTaskNickNameResult {
  taskNickName: string;
  setTaskNickName: (newTaskNickName: string) => void;
  taskNickNameEntities: string;
  setTaskNickNameEntities: (newTaskNickNameEntities: string) => void;
  taskNickNameAction: string;
  setTaskNickNameAction: (newTaskNickNameAction: string) => void;
  taskNickNameUserModified: boolean;
  setTaskNickNameUserModified: (newTaskNickNameModified: boolean) => void;
}

/**
 * Re-usable hook for managing the bulk task nickname.
 */
export const useTaskNickName = ({
  entityType,
  actions,
  actionType,
  identifierTypes,
  selectedIdentifierType,
  hasFile,
  bulkImportParams,
  selectedRowEntities,
  entityIdentifiers,
  areTotalRowsSelected,
  totalRowsCount,
  pastedIdCount
}): UseTaskNickNameResult => {
  const [taskNickName, setTaskNickName]                         = useState<string>('');
  const [taskNickNameEntities, setTaskNickNameEntities]         = useState<string>('');
  const [taskNickNameAction, setTaskNickNameAction]             = useState<string>('');
  const [taskNickNameUserModified, setTaskNickNameUserModified] = useState<boolean>(false);
  const {
    singularEntityLabel
  } = useEntityLabel(entityType);

  const getIdentifierTypeLabel = (identifierType: string): string => (
    identifierTypes.find(type => type.value === identifierType)?.label
  );

  const updateTaskNickName = () => {
    if (!taskNickNameUserModified) {
      let updated = actions.find(action => action.type === actionType)?.title || '';
      const jobNameTimestamp = `; ${(timeFormatter(new Date(), 'YYYYMMDD_HHmmss', true))}`;
      if (taskNickNameEntities.length || taskNickNameAction.length) {
        updated += ': ';
        if (taskNickNameEntities.length) {
          updated += taskNickNameEntities;
          if (taskNickNameAction.length) {
            updated += ` ${taskNickNameAction}`;
          }
        } else {
          updated += ` ${taskNickNameAction}`;
        }
        updated += jobNameTimestamp;
      }
      setTaskNickName(updated);
    }
  };

  useEffect(() => {
    if (taskNickNameAction.length) {
      setTaskNickNameAction('');
    }
    setTaskNickNameUserModified(false);
  }, [actionType]);

  useEffect(() => {
    updateTaskNickName();
  }, [actionType, taskNickNameEntities, taskNickNameAction]);

  useEffect(() => {
    if (hasFile && bulkImportParams?.fileName && selectedIdentifierType) {
      const {fileName, items} = bulkImportParams;
      const updated = translate('common:editors.bulkActions.activityName.entities.file', {
        count : items?.length,
        type  : getIdentifierTypeLabel(selectedIdentifierType) || singularEntityLabel,
        fileName
      });
      setTaskNickNameEntities(updated);
    }
  }, [hasFile, bulkImportParams, selectedIdentifierType]);

  useEffect(() => {
    if (!hasFile && pastedIdCount && selectedIdentifierType) {
      const updated = translate('common:editors.bulkActions.activityName.entities.pasted', {
        count : pastedIdCount,
        type  : getIdentifierTypeLabel(selectedIdentifierType) || singularEntityLabel
      });
      setTaskNickNameEntities(updated);
    }
  }, [hasFile, pastedIdCount, selectedIdentifierType]);

  useEffect(() => {
    if (areTotalRowsSelected || selectedRowEntities?.length) {
      const updated = translate('common:editors.bulkActions.activityName.entities.selected', {
        count : areTotalRowsSelected ? totalRowsCount : entityIdentifiers.length,
        type  : singularEntityLabel
      });
      setTaskNickNameEntities(updated);
    }
  }, [areTotalRowsSelected, totalRowsCount, selectedRowEntities, entityIdentifiers]);

  return {
    taskNickName,
    setTaskNickName,
    taskNickNameEntities,
    setTaskNickNameEntities,
    taskNickNameAction,
    setTaskNickNameAction,
    taskNickNameUserModified,
    setTaskNickNameUserModified
  };
};
